import { useEffect, useMemo, useState } from "react";
import { Activity, Cpu, HardDrive, MemoryStick, Network, Server, Database } from "lucide-react";
import { api, fmtBytes, fmtUptime } from "@/lib/api";

const REFRESH_MS = 5000;

export function levelOf(pct) {
  if (pct >= 97) return "full";
  if (pct >= 90) return "crit";
  if (pct >= 70) return "warn";
  return "ok";
}

const LEVEL = {
  ok: { bar: "bg-emerald-500", text: "text-emerald-400", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "Saudável" },
  warn: { bar: "bg-amber-500", text: "text-amber-400", badge: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Atenção" },
  crit: { bar: "bg-red-500", text: "text-red-400", badge: "bg-red-500/15 text-red-300 border-red-500/30", label: "Crítico" },
  full: { bar: "bg-red-600 animate-pulse", text: "text-red-400", badge: "bg-red-600/20 text-red-200 border-red-500/50", label: "Cheio" },
};

const fmtRate = (b = 0) => `${fmtBytes(b)}/s`;
const pct1 = (p = 0) => `${Number(p).toFixed(p >= 10 ? 0 : 1)}%`;

export function UsageBar({ percent = 0, level, className = "", testId }) {
  const lv = LEVEL[level || levelOf(percent)];
  return (
    <div className={`h-2 w-full rounded-sm bg-zinc-800 overflow-hidden ${className}`} data-testid={testId}>
      <div className={`h-full ${lv.bar} transition-all duration-700`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
    </div>
  );
}

function StatusBadge({ percent, testId }) {
  const lv = LEVEL[levelOf(percent)];
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${lv.badge}`} data-testid={testId}>{lv.label}</span>;
}

function Card({ icon: Icon, title, right, children, testId, className = "" }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 flex flex-col gap-3 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400"><Icon className="h-3.5 w-3.5 text-orange-500" /> {title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function DiskCard({ d, io }) {
  const lv = LEVEL[levelOf(d.percent)];
  const inodePct = d.inodes ? (d.inodes_used / d.inodes) * 100 : 0;
  return (
    <Card icon={HardDrive} title={d.mount === "/" ? "Disco raiz" : "Disco"} testId={`disk-card-${d.mount.replace(/\//g, "_") || "root"}`}
      right={<div className="flex items-center gap-1.5">{d.holds_data && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-orange-500/40 bg-orange-500/10 text-orange-300 inline-flex items-center gap-1" data-testid="disk-holds-data"><Database className="h-3 w-3" /> dados AxonDB</span>}<StatusBadge percent={d.percent} /></div>}>
      <div>
        <div className="font-mono text-lg text-white truncate" title={d.mount}>{d.mount}</div>
        <div className="text-xs text-zinc-500 font-mono truncate">{d.device} · {d.fstype}</div>
      </div>
      <div className="flex items-end justify-between">
        <div className={`font-display text-3xl font-bold tracking-tight ${lv.text}`}>{pct1(d.percent)}</div>
        <div className="text-right text-xs text-zinc-400">
          <div><span className="text-zinc-200">{fmtBytes(d.used)}</span> usados de {fmtBytes(d.total)}</div>
          <div><span className="text-zinc-200">{fmtBytes(d.available)}</span> livres</div>
        </div>
      </div>
      <UsageBar percent={d.percent} />
      <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-[11px] text-zinc-500 font-mono">
        <span>inodes {pct1(inodePct)}</span>
        {io ? <span>↓ {fmtRate(io.read_bps)} · ↑ {fmtRate(io.write_bps)} · ocupado {pct1(io.busy_percent)}</span> : <span>I/O n/d</span>}
      </div>
    </Card>
  );
}

function matchIo(disk, devices = []) {
  const base = (disk.device || "").split("/").pop();
  if (!base) return null;
  let best = null;
  for (const dev of devices) {
    if (base.startsWith(dev.device) && (!best || dev.device.length > best.device.length)) best = dev;
  }
  return best;
}

function SectionTitle() {
  return (
    <div className="mb-4">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Server className="h-4 w-4 text-orange-500" /> Servidor</h2>
    </div>
  );
}

function Sk({ className = "" }) {
  return <div className={`rounded-sm bg-zinc-800/80 animate-pulse ${className}`} />;
}

function SkeletonCard({ bars = 1, cores = false }) {
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 flex flex-col gap-3" aria-hidden="true">
      <div className="flex items-center justify-between"><Sk className="h-3 w-24" /><Sk className="h-4 w-16" /></div>
      <div className="flex items-end justify-between">
        <Sk className="h-9 w-20" />
        <div className="flex flex-col items-end gap-1.5"><Sk className="h-3 w-28" /><Sk className="h-3 w-24" /></div>
      </div>
      {Array.from({ length: bars }).map((_, i) => <Sk key={i} className="h-2 w-full" />)}
      {cores && <div className="flex gap-0.5 h-6 items-end">{Array.from({ length: 8 }).map((_, i) => <Sk key={i} className="flex-1 h-full rounded-[1px]" />)}</div>}
      <Sk className="h-3 w-3/4" />
    </div>
  );
}

function SkeletonDisk() {
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 flex flex-col gap-3" aria-hidden="true">
      <div className="flex items-center justify-between"><Sk className="h-3 w-16" /><Sk className="h-4 w-20" /></div>
      <div className="space-y-1.5"><Sk className="h-6 w-24" /><Sk className="h-3 w-40" /></div>
      <div className="flex items-end justify-between">
        <Sk className="h-9 w-16" />
        <div className="flex flex-col items-end gap-1.5"><Sk className="h-3 w-36" /><Sk className="h-3 w-24" /></div>
      </div>
      <Sk className="h-2 w-full" />
      <div className="flex justify-between"><Sk className="h-3 w-16" /><Sk className="h-3 w-40" /></div>
    </div>
  );
}

function MonitorSkeleton() {
  return (
    <section className="mt-10" data-testid="server-monitor-loading">
      <SectionTitle />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SkeletonCard cores />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <div className="flex items-center justify-between mt-6 mb-3">
        <h3 className="font-display text-base font-semibold flex items-center gap-2"><HardDrive className="h-4 w-4 text-orange-500" /> Discos</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <SkeletonDisk />
        <SkeletonDisk />
      </div>
    </section>
  );
}

export default function ServerMonitor() {
  const [sys, setSys] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/system").then((r) => { if (alive) { setSys(r.data); setErr(null); } }).catch((e) => alive && setErr(e?.response?.data?.detail || e.message));
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const disks = useMemo(() => sys?.disks || [], [sys]);
  const worstDisk = useMemo(() => disks.reduce((m, d) => Math.max(m, d.percent), 0), [disks]);

  if (err && !sys) {
    return <div className="mt-10 rounded-sm border border-zinc-800 p-4 text-sm text-zinc-400" data-testid="server-monitor-error">Monitoramento indisponível: {err}</div>;
  }
  if (!sys) return <MonitorSkeleton />;

  const { cpu, memory: mem, network: net, disk_io: dio, process: proc } = sys;
  const cpuLv = LEVEL[levelOf(cpu.percent)];
  const memLv = LEVEL[levelOf(mem.percent)];
  const alerts = [
    ...disks.filter((d) => levelOf(d.percent) !== "ok").map((d) => `disco ${d.mount} em ${pct1(d.percent)}`),
    ...(levelOf(mem.percent) !== "ok" ? [`memória em ${pct1(mem.percent)}`] : []),
    ...(levelOf(cpu.percent) !== "ok" ? [`CPU em ${pct1(cpu.percent)}`] : []),
  ];

  return (
    <section className="mt-10" data-testid="server-monitor">
      <SectionTitle />

      {alerts.length > 0 && (
        <div className={`mb-4 rounded-sm border p-3 text-sm flex items-center gap-2 ${worstDisk >= 90 || levelOf(mem.percent) === "crit" ? "border-red-500/40 bg-red-500/10 text-red-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`} data-testid="server-alerts">
          <Activity className="h-4 w-4 shrink-0" /> Atenção: {alerts.join(" · ")}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card icon={Cpu} title="CPU" testId="cpu-card" right={<StatusBadge percent={cpu.percent} testId="cpu-status" />}>
          <div className="flex items-end justify-between">
            <div className={`font-display text-3xl font-bold tracking-tight ${cpuLv.text}`} data-testid="cpu-percent">{pct1(cpu.percent)}</div>
            <div className="text-right text-xs text-zinc-400">
              <div><span className="text-zinc-200">{cpu.cores}</span> núcleos</div>
              <div>carga {cpu.load_1.toFixed(2)} · {cpu.load_5.toFixed(2)} · {cpu.load_15.toFixed(2)}</div>
            </div>
          </div>
          <UsageBar percent={cpu.percent} />
          <div className="flex gap-0.5 h-6 items-end" title="uso por núcleo" data-testid="cpu-cores">
            {cpu.per_core.slice(0, 64).map((p, i) => (
              <div key={i} className="flex-1 bg-zinc-800 rounded-[1px] overflow-hidden flex items-end h-full"><div className={`w-full ${LEVEL[levelOf(p)].bar}`} style={{ height: `${Math.max(4, p)}%` }} /></div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 truncate font-mono" title={cpu.model}>{cpu.model}</div>
        </Card>

        <Card icon={MemoryStick} title="Memória RAM" testId="memory-card" right={<StatusBadge percent={mem.percent} testId="memory-status" />}>
          <div className="flex items-end justify-between">
            <div className={`font-display text-3xl font-bold tracking-tight ${memLv.text}`} data-testid="memory-percent">{pct1(mem.percent)}</div>
            <div className="text-right text-xs text-zinc-400">
              <div><span className="text-zinc-200">{fmtBytes(mem.used)}</span> de {fmtBytes(mem.total)}</div>
              <div><span className="text-zinc-200">{fmtBytes(mem.available)}</span> disponíveis</div>
            </div>
          </div>
          <UsageBar percent={mem.percent} />
          <div className="text-[11px] text-zinc-500 font-mono flex flex-wrap justify-between gap-x-3">
            <span>cache {fmtBytes(mem.cached)}</span>
            <span>{mem.swap_total > 0 ? `swap ${fmtBytes(mem.swap_used)} / ${fmtBytes(mem.swap_total)} (${pct1(mem.swap_percent)})` : "sem swap"}</span>
          </div>
          {mem.swap_total > 0 && <UsageBar percent={mem.swap_percent} className="h-1" />}
          {mem.limited_by_cgroup && <div className="text-[11px] text-zinc-500">limite do container aplicado</div>}
        </Card>

        <Card icon={Network} title="Rede" testId="network-card" right={<span className="text-[10px] text-zinc-500 font-mono">{net.interfaces.length} interface(s)</span>}>
          <div className="grid grid-cols-2 gap-2">
            <div><div className="text-[11px] text-zinc-500">↓ entrada</div><div className="font-display text-xl font-bold text-white" data-testid="net-rx">{fmtRate(net.rx_bps)}</div></div>
            <div><div className="text-[11px] text-zinc-500">↑ saída</div><div className="font-display text-xl font-bold text-white" data-testid="net-tx">{fmtRate(net.tx_bps)}</div></div>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono space-y-0.5">
            {net.interfaces.slice(0, 3).map((i) => <div key={i.iface} className="flex justify-between"><span>{i.iface}</span><span>total ↓ {fmtBytes(i.rx_total)} · ↑ {fmtBytes(i.tx_total)}</span></div>)}
          </div>
          <div className="border-t border-zinc-800 pt-2 text-[11px] text-zinc-500 font-mono flex justify-between"><span>I/O disco</span><span>↓ {fmtRate(dio.read_bps)} · ↑ {fmtRate(dio.write_bps)}</span></div>
        </Card>

        <Card icon={Activity} title="Processo axond" testId="process-card" right={<span className="text-[10px] text-zinc-500 font-mono">pid ativo há {fmtUptime(proc.uptime_secs)}</span>}>
          <div className="flex items-end justify-between">
            <div className="font-display text-3xl font-bold tracking-tight text-white" data-testid="process-rss">{fmtBytes(proc.rss)}</div>
            <div className="text-right text-xs text-zinc-400">
              <div>RSS · <span className="text-zinc-200">{pct1(proc.cpu_percent)}</span> de CPU</div>
              <div>{proc.threads} threads · {proc.open_fds} fds</div>
            </div>
          </div>
          <UsageBar percent={mem.total ? (proc.rss / mem.total) * 100 : 0} level="ok" />
          <div className="text-[11px] text-zinc-500 font-mono">{mem.total ? pct1((proc.rss / mem.total) * 100) : "—"} da RAM do servidor · {proc.databases} banco(s) aberto(s)</div>
        </Card>
      </div>

      <div className="flex items-center justify-between mt-6 mb-3">
        <h3 className="font-display text-base font-semibold flex items-center gap-2"><HardDrive className="h-4 w-4 text-orange-500" /> Discos <span className="text-zinc-500 font-normal text-sm">({disks.length})</span></h3>
        <div className="hidden sm:flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-emerald-500 inline-block" /> &lt;70%</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-amber-500 inline-block" /> 70–90%</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-red-500 inline-block" /> &gt;90%</span>
        </div>
      </div>
      {disks.length === 0 ? (
        <div className="rounded-sm border border-zinc-800 p-6 text-sm text-zinc-500 text-center" data-testid="disks-empty">Nenhum disco detectado.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="disks-grid">
          {disks.map((d) => <DiskCard key={d.mount} d={d} io={matchIo(d, dio.devices)} />)}
        </div>
      )}
    </section>
  );
}
